import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/state/AuthContext";

export function LoginPage() {
  const { token, login, selectTenant, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingOrgs, setPendingOrgs] = useState<
    { tenantId: string; tenantName: string; roleLabel: string }[] | null
  >(null);

  if (token && !loading && !pendingOrgs) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (!result.success) {
      setError(result.error ?? "Login failed");
      return;
    }
    if (result.requiresTenantSelection && result.memberships) {
      setPendingOrgs(result.memberships);
    }
  };

  const pickOrg = async (tenantId: string) => {
    setSubmitting(true);
    setError("");
    const result = await selectTenant(email.trim(), password, tenantId);
    setSubmitting(false);
    if (!result.success) setError(result.error ?? "Failed");
  };

  if (pendingOrgs) {
    return (
      <div className="login-bg min-h-svh flex items-center justify-center p-4">
        <div className="login-card w-full max-w-md bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-xl font-bold text-[#1e3a4f] mb-4">Select Organization</h1>
          <ul className="space-y-2">
            {pendingOrgs.map((m) => (
              <li key={m.tenantId}>
                <Button
                  className="w-full justify-start bg-[#1e3a4f] hover:bg-[#163041]"
                  disabled={submitting}
                  onClick={() => pickOrg(m.tenantId)}
                >
                  {m.tenantName}
                  <span className="ml-auto text-xs opacity-80">{m.roleLabel}</span>
                </Button>
              </li>
            ))}
          </ul>
          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="login-bg min-h-svh flex items-center justify-center p-4">
      <div className="login-card w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-center text-[#1e3a4f] mb-6">InFloTrack</h1>
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" asChild>
              <Link to="/signup">Create Account</Link>
            </Button>
            <Button type="submit" className="flex-1 bg-[#1e3a4f] hover:bg-[#163041]" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
